import AddIcon from '@mui/icons-material/Add'
import { Backdrop, Box, CircularProgress, Grid, Stack, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { Typography } from 'antd'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useUpdateOrganizationField } from 'api/useHooks/useUpdateOrgField'
import CustomFiltersModal from 'components/DataGrid/components/CustomFiltersModal'
import MultiUserSelect from 'components/Forms/MultiUserSelect'
import CreateOptimizationButton from 'components/ui/create-optimization-button'
import CustomFiltersMenu from 'components/ui/custom-filters-menu-button'
import GeneralCreateButton from 'components/ui/general-create-button'
import SaveNewTeam from 'components/ui/save-new-team'
import { isEqual } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { BoxScroller } from 'styles/elements/styleElements'
import { Insight, Observation, Optimization, OptimizationDetails, User } from 'types'
import { toIdArray } from 'utils/toIdArray'
import { useDateOperators } from 'utils/useDateOperators'
import { useOptsByOrgId } from '../../api/aws/optimizations/useOptsByOrgId'
import { usePostOptimization } from '../../api/aws/optimizations/usePostOptimization'
import { useUpdateOptimization } from '../../api/aws/optimizations/useUpdateOptimization'
import OptimizationsModal from './CreateOptimizationsModal'
import SavedOptimization from './SavedOptimization'

interface OptimizationEntry extends Optimization {
  PK: string
  opt_uuid: string
  orgId: string
  addtlNotes: string
  opts: number
  percentage: number
  followDate: string
  id: string
  createdAt: string
  type: string
  owner?: string
}
interface InsightEntry extends Insight {
  createdAt: string
}
interface ObservationEntry extends Observation {
  createdAt: string
}

const OptimizationsContainer = ({ organization, context, expanded, monthKey, onExpandedIdChange }) => {
  const theme = useTheme()
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
  const { byMonth } = useDateOperators()
  const updateOrgField = useUpdateOrganizationField(
    organization ? { PK: organization.PK, SK: organization.SK, org_uuid: organization.org_uuid } : undefined
  )
  const queryClient = useQueryClient()
  const [open, setOptOpen] = useState<boolean>(false)
  const [isCreating, setIsCreating] = useState(false)
  const [expandedId, setExpandedIdInternal] = useState<string | null>(null)

  // Wrap setExpandedId to notify parent
  const setExpandedId = (id: string | null) => {
    setExpandedIdInternal(id)
    onExpandedIdChange?.(id)
  }
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  // const [optimizations, setOptimizations] = useState<OptimizationEntry[]>(organization?.optimizations || [])
  const { data: optimizations, isLoading: isOptimizationsLoading } = useOptsByOrgId(organization?.org_uuid)
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const filteredOpts = useMemo(() => {
    if (!optimizations || isOptimizationsLoading) return []
    return optimizations?.filter(o => byMonth(o?.createdAt))
  }, [optimizations, isOptimizationsLoading])
  const [localOptimizations, setLocalOptimizations] = useState<OptimizationEntry[]>([])
  const [localOrgTeam, setLocalOrgTeam] = useState<User[] | undefined>(undefined)
  const [selectedType, setSelectedType] = useState('')
  const [optionsOpen, setOptionsOpen] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [insights, setInsights] = useState<InsightEntry[]>(organization?.insights || [])
  const { mutate: updateOptimization } = useUpdateOptimization()
  const { mutate: createOptimization } = usePostOptimization()
  const [selectedOptimization, setSelectedOptimization] = useState(null)
  const [highlight, setHighlight] = useState(false)
  const [optimizationDetails, setOptimizationDetails] = useState<OptimizationDetails>({
    optimization: null,
    addtlNotes: '',
    opts: 0,
    percentage: 0,
    followDate: null,
    owner: currentUser?.user_uuid,
  })
  const mtbTeam: User[] = useMemo(() => {
    if (!users || isUsersLoading) return []
    const ids = toIdArray(organization?.team_details?.map(t => t?.user_uuid)) // <-- robust
    if (!ids.length) return []
    const byId = new Map(users.map(u => [u.user_uuid, u]))
    return ids.map(id => byId.get(id)).filter(Boolean) as User[]
  }, [users, isUsersLoading, organization?.team_details])

  useEffect(() => {
    if (filteredOpts) {
      setLocalOptimizations(filteredOpts ?? [])
    }
  }, [filteredOpts])
  useEffect(() => {
    if (expandedId && filteredOpts) {
      const selectedOptimization = filteredOpts?.find((o: Optimization) => o?.id === expandedId)
      setSelectedOptimization(selectedOptimization)
    }
  }, [expandedId, filteredOpts])
  useEffect(() => {
    if (localOrgTeam === undefined) {
      setLocalOrgTeam(mtbTeam)
    }
  }, [mtbTeam, localOrgTeam])
  const handlePostOptimization = (data: {
    optimization: Optimization | null
    addtlNotes: string
    opts: number
    percentage: number
    followDate: string
    type: string
  }) => {
    if (!data.optimization) return
    const newUuid = crypto.randomUUID()
    const newEntry: OptimizationEntry = {
      ...data.optimization,
      PK: `ORG_${organization?.org_uuid}`,
      orgId: organization?.org_uuid,
      addtlNotes: data.addtlNotes,
      opts: data.opts,
      percentage: data.percentage,
      followDate: data.followDate,
      id: newUuid,
      opt_uuid: newUuid,
      createdAt: new Date().toISOString(),
      type: data.type,
      owner: currentUser?.user_uuid,
    }
    console.log('newEntry', newEntry)
    // Optionally, if you're relying on your fetch hook to get optimizations,
    // you might not need to update local state manually.
    // Instead, call the mutation to post the new optimization:
    createOptimization(newEntry, {
      onSuccess: () => {
        console.log('✅ Successfully created optimization')
        setIsCreating(false)
        setOptOpen(false)
        setAnchorEl(null) // Ensure popover anchor is cleared
        setOptionsOpen(false) // Ensure popover is closed
        queryClient.invalidateQueries({ queryKey: ['optsByOrgId', organization?.org_uuid] })
      },
      onError: error => {
        console.error('❌ Error creating optimization:', error)
        setIsCreating(false)
      },
    })
  }
  const openOptimizationOptions = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget) // Attach the popover to the button
    setOptionsOpen(true)
  }
  const closeOptimizationOptions = () => {
    setAnchorEl(null) // Attach the popover to the button
    setOptionsOpen(false)
  }
  // Update optimization fields (addtlNotes, opts) in state
  const handleUpdateOptimization = (index: number, field: keyof OptimizationEntry, value: string | number) => {
    setLocalOptimizations(prevOptimizations =>
      prevOptimizations.map((opt, i) => (i === index ? { ...opt, [field]: value } : opt))
    )
  }
  const openInsight = () => {
    // setInsightOpen(true)
    setOptOpen(true)
    setSelectedType('insight')
    closeOptimizationOptions()
  }
  const openObservation = () => {
    // setObservationOpen(true)
    setOptOpen(true)
    setSelectedType('observation')
    closeOptimizationOptions()
  }
  const openLeasingPercentage = () => {
    // setObservationOpen(true)
    setOptOpen(true)
    setSelectedType('leasingPercentage')
    closeOptimizationOptions()
  }
  const handleSaveChanges = async (optIdentifiers: Optimization[], updatedData: Partial<Optimization>) => {
    console.log('optIdentifiers', optIdentifiers, 'updatedData', updatedData)
    if (!optIdentifiers.length) return

    // Assume updatedData includes PK and SK. Extract them:
    const { PK, SK, ...fieldsToUpdate } = updatedData

    // Validate that PK and SK exist, if not, fall back to optIdentifiers
    const finalPK = PK || optIdentifiers[0].PK
    const finalSK = SK || optIdentifiers[0].SK

    if (!finalPK || !finalSK) {
      console.warn('[handleSaveChanges] No valid keys provided.')
      return
    }

    const updatePayload = {
      optPK: finalPK,
      optSK: finalSK,
      updatedData: fieldsToUpdate,
      orgId: organization?.org_uuid ?? '',
    }
    console.log('Data used for update (single update): ', updatePayload)

    updateOptimization(updatePayload, {
      onSuccess: () => {
        // Update local state AND react-query cache for immediate UI update
        setLocalOptimizations(prev =>
          prev.map(opt => (opt.PK === finalPK && opt.SK === finalSK ? { ...opt, ...fieldsToUpdate } : opt))
        )
        queryClient.setQueryData<OptimizationEntry[]>(['optsByOrgId', organization?.org_uuid], oldData => {
          if (!oldData) return oldData
          return oldData.map(opt => (opt.PK === finalPK && opt.SK === finalSK ? { ...opt, ...fieldsToUpdate } : opt))
        })

        // queryClient.invalidateQueries({ queryKey: ['optsByOrgId', organization?.org_uuid] })
      },
      onError: error => console.error('[handleSaveChanges] Single update failed', error),
    })
  }

  const handleClose = () => {
    setOptOpen(false)
    setLocalOptimizations(localOptimizations || [])
    setOptimizationDetails({
      optimization: null,
      addtlNotes: '',
      opts: 0,
      followDate: null,
      owner: null,
    })
    setSelectedType('')
  }
  const usersIndex = React.useMemo(() => new Map((users ?? []).map(u => [u.user_uuid, u])), [users])

  // hook, bound to this org

  // when saving team selection from MultiUserSelect:
  const handleSave = async () => {
    if (!localOrgTeam) return
    // setIsSaving(true)
    try {
      await updateOrgField('team_details', localOrgTeam, { usersIndex })
      // optional: snackbar, etc.
    } finally {
      // setIsSaving(false)
    }
  }
  return (
    <Box sx={{ py: 2, gap: 2, position: 'relative' }}>
      {/* Backdrop when accordion is expanded - covers the entire screen including navbar */}
      <Backdrop
        open={!!expandedId}
        onClick={() => setExpandedId(null)}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1400, // Above navbar (1300) but below expanded accordion
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      />
      <Grid
        container
        p={2}
        gap={1}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          transition: 'all ease 0.3s',
        }}
      >
        <Grid container sx={{ px: 0.5 }}>
          <Grid
            item
            xs={isEqual(localOrgTeam, mtbTeam || []) ? 12 : 9}
            sx={t => ({
              transition: t.transitions.create(['flex-basis', 'max-width'], {
                duration: 200,
                easing: t.transitions.easing.easeInOut,
              }),
            })}
          >
            <MultiUserSelect
              name="team_details"
              value={localOrgTeam ?? mtbTeam ?? []}
              options={users ?? []}
              displayLabel="Team"
              onChange={newUsers => setLocalOrgTeam(newUsers)}
              singleSelect={false}
              isGridUpdate={false}
              max={8}
            />
          </Grid>
          <Grid
            item
            xs={3}
            sx={t => ({
              transition: t.transitions.create(['flex-basis', 'max-width'], {
                duration: 200,
                easing: t.transitions.easing.easeInOut,
              }),
            })}
          >
            <SaveNewTeam newTeam={localOrgTeam} currTeam={mtbTeam} onSave={handleSave} />
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ justifyContent: 'flex-start' }}>
          <Box display={'flex'}>
            <CustomFiltersMenu
              icon={<AddIcon sx={{ fontSize: '30px' }} />}
              onOpenCustomFilters={openOptimizationOptions}
              count={0}
              badgeColor={'icon.mtb'}
              helperRef={null}
              tooltipTitle={'Create Options'}
            />
          </Box>
        </Grid>
        <BoxScroller
          ref={scrollContainerRef}
          data-scroll-container
          sx={{
            maxHeight: expanded ? 'calc(100vh - 410px)' : 'calc(100vh - 290px)',
            scrollBehavior: 'smooth', // smooth scrolling
            scrollSnapType: 'y proximity', // optional snap
            width: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {isOptimizationsLoading ? (
            <CircularProgress />
          ) : (
            <>
              <Grid item xs={12}>
                <Grid item xs={12}>
                  {localOptimizations?.length > 0 ? (
                    localOptimizations.map((item, index) => (
                      <SavedOptimization
                        key={item.id}
                        optimization={item}
                        addtlNotes={item.addtlNotes}
                        onUpdate={(field, value) => handleUpdateOptimization(index, field, value)}
                        onSave={handleSaveChanges}
                        context={context}
                        expandedId={expandedId}
                        setExpandedId={setExpandedId}
                        highlight={highlight}
                        setHighlight={setHighlight}
                      />
                    ))
                  ) : (
                    <Typography>No optimizations saved yet.</Typography>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {open && (
                  <OptimizationsModal
                    open={open}
                    onClose={handleClose}
                    onSave={handlePostOptimization}
                    organizationName={organization?.name || ''}
                    selectedType={selectedType}
                    setSelectedType={setSelectedType}
                    optimizationDetails={optimizationDetails}
                    setOptimizationDetails={setOptimizationDetails}
                    context={context}
                  />
                )}
              </Grid>
            </>
          )}
        </BoxScroller>
      </Grid>
      <CustomFiltersModal isOpen={optionsOpen} anchorEl={anchorEl} handleCloseCustomFilters={closeOptimizationOptions}>
        <Stack spacing={1}>
          <Grid item xs={12}>
            <CreateOptimizationButton
              onClick={() => {
                setOptOpen(true)
                closeOptimizationOptions()
              }}
              isCreating={isCreating}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12}>
            <GeneralCreateButton
              text={'Insights'}
              customColor={'icon.insight'}
              // icon={<InsightsIcon sx={{ fontSize: '30px' }} />}
              onClick={openInsight}
              isCreating={isCreating}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12}>
            <GeneralCreateButton
              text={'Observations'}
              customColor={'icon.observation'}
              // icon={<VisibilityIcon sx={{ fontSize: '30px' }} />}
              onClick={openObservation}
              isCreating={isCreating}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12}>
            <GeneralCreateButton
              text={'Leasing Percentage'}
              customColor={'icon.leasingPercentage'}
              // icon={<VisibilityIcon sx={{ fontSize: '30px' }} />}
              onClick={openLeasingPercentage}
              isCreating={isCreating}
              disabled={false}
            />
          </Grid>
        </Stack>
      </CustomFiltersModal>
    </Box>
  )
}

export default OptimizationsContainer
